import React from "react";
import { v4 as uuidv4 } from "uuid";

import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { HStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { useToast } from "@UI/hooks/useToast";
import useMediaQuery from "@UI/hooks/useMediaQuery";

/**
 * The flow's photo toast: `AlertBox` in its error skin — white card, red rule
 * down its left edge, warning triangle, and an × that dismisses it.
 *
 * Lifted out of `PhotoUploader`, which owned the only copy, so the description
 * screen's photo errors are the same object as the photos screen's — Figma
 * draws one toast for both.
 *
 * A fresh id per call, so two failures stack rather than replacing one
 * another: each is about different files, and each clears itself in two
 * seconds anyway.
 */
export const usePhotoToast = () => {
  const toast = useToast();
  const { isMobile } = useMediaQuery();

  return (description: string) => {
    const toastId = uuidv4();

    toast({
      id: toastId,
      status: "error",
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status="error"
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={isMobile ? "initial" : "80px"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };
};
